import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Markdown } from "@/components/Markdown";
import { ArrowLeft, Loader2, Save, Download, Eye, Pencil, Sparkles, FileStack } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const sourceLabels = { ai: "Généré par IA", word: "Importé (Word)", pdf: "Importé (PDF)", template: "Modèle" };

export default function ContractContentEditor({ user, setUser }) {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [genTpl, setGenTpl] = useState(false);
  const pollRef = useRef(null);

  async function generateTemplate() {
    setGenTpl(true);
    toast.loading("Analyse du contrat et génération du questionnaire...", { id: "tpl" });
    try {
      const res = await fetch(`${API_URL}/api/contracts/${contractId}/to-template`, { method: "POST", credentials: "include" });
      if (!res.ok) { const e = await res.json(); toast.dismiss("tpl"); toast.error(e.detail || "Erreur"); setGenTpl(false); return; }
      const { template_id } = await res.json();
      const poll = setInterval(async () => {
        try {
          const t = await fetch(`${API_URL}/api/templates/${template_id}`, { credentials: "include" });
          if (t.ok) {
            const tpl = await t.json();
            if (tpl.status === "ready") {
              clearInterval(poll); toast.dismiss("tpl"); setGenTpl(false);
              toast.success(`Modèle créé avec ${tpl.fields?.length || 0} champ(s)`);
              navigate(`/contracts/new/${template_id}`);
            } else if (tpl.status === "failed") {
              clearInterval(poll); toast.dismiss("tpl"); setGenTpl(false);
              toast.error("Échec de la génération du modèle");
            }
          }
        } catch (e) { /* keep polling */ }
      }, 4000);
      setTimeout(() => { clearInterval(poll); setGenTpl(false); }, 180000);
    } catch (e) { toast.dismiss("tpl"); toast.error("Erreur de connexion"); setGenTpl(false); }
  }

  const fetchContract = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/contracts/${contractId}`, { credentials: "include" });
      if (res.ok) {
        const c = await res.json();
        setContract(c);
        setName(c.name);
        setContent(c.content || "");
        return c;
      }
    } catch (e) { toast.error("Erreur de chargement"); }
    return null;
  }, [contractId]);

  useEffect(() => {
    (async () => { await fetchContract(); setLoading(false); })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchContract]);

  // Poll while AI is generating
  useEffect(() => {
    if (contract?.ai_status === "generating" && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const c = await fetchContract();
        if (c && c.ai_status !== "generating") {
          clearInterval(pollRef.current); pollRef.current = null;
          if (c.ai_status === "ready") toast.success("Contrat généré !");
          else if (c.ai_status === "failed") toast.error("Échec de la génération");
        }
      }, 4000);
    }
  }, [contract, fetchContract]);

  async function save() {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/contracts/${contractId}/content`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, content })
      });
      if (res.ok) { const c = await res.json(); setContract(c); setEditMode(false); toast.success("Enregistré (v" + c.version + ")"); }
      else toast.error("Erreur");
    } catch (e) { toast.error("Erreur"); }
    setSaving(false);
  }

  async function download(format) {
    try {
      const res = await fetch(`${API_URL}/api/contracts/${contractId}/generate?format=${format}`, { method: "POST", credentials: "include" });
      if (res.ok) { const d = await res.json(); window.open(`${API_URL}${d.file_url}`, "_blank"); }
      else toast.error("Erreur de génération");
    } catch (e) { toast.error("Erreur"); }
  }

  if (loading) return <AppLayout user={user} setUser={setUser}><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div></AppLayout>;
  if (!contract) return <AppLayout user={user} setUser={setUser}><div className="p-8">Contrat introuvable.</div></AppLayout>;

  const generating = contract.ai_status === "generating";

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8 max-w-4xl mx-auto" data-testid="contract-content-editor">
        <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate("/contracts")} data-testid="back-to-contracts">
          <ArrowLeft className="w-4 h-4 mr-2" />Retour aux contrats
        </Button>

        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight">{contract.name}</h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline">{sourceLabels[contract.source] || contract.source}</Badge>
              <Badge variant="outline">v{contract.version}</Badge>
            </div>
          </div>
          {!generating && (
            <div className="flex items-center gap-2">
              {editMode ? (
                <>
                  <Button variant="outline" onClick={() => { setEditMode(false); setContent(contract.content || ""); setName(contract.name); }}>Annuler</Button>
                  <Button onClick={save} disabled={saving} data-testid="save-content-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer</Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setEditMode(true)} data-testid="edit-content-btn"><Pencil className="w-4 h-4 mr-2" />Modifier</Button>
                  <Button variant="outline" onClick={generateTemplate} disabled={genTpl} data-testid="to-template-btn">
                    {genTpl ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileStack className="w-4 h-4 mr-2" />}Générer un modèle
                  </Button>
                  <Button variant="outline" onClick={() => download("docx")} data-testid="download-docx-btn"><Download className="w-4 h-4 mr-2" />Word</Button>
                  <Button variant="outline" onClick={() => download("pdf")} data-testid="download-pdf-btn"><Download className="w-4 h-4 mr-2" />PDF</Button>
                </>
              )}
            </div>
          )}
        </div>

        {generating ? (
          <Card><CardContent className="py-16 text-center">
            <Sparkles className="w-10 h-10 text-blue-500 mx-auto mb-3" />
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> L'IA rédige votre contrat (cela peut prendre 1 à 2 minutes)...
            </div>
          </CardContent></Card>
        ) : editMode ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Nom du contrat</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="content-name-input" />
            </div>
            <Textarea value={content} onChange={(e) => setContent(e.target.value)} className="min-h-[500px] font-mono text-xs" data-testid="content-textarea" />
          </div>
        ) : (
          <Card><CardContent className="pt-6">
            <Markdown content={contract.content} />
          </CardContent></Card>
        )}
      </div>
    </AppLayout>
  );
}
